import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';

interface PieChartData {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartData[];
  title: string;
}

const RADIAN = Math.PI / 180;

// Функция для определения яркости цвета (0-255)
const getLuminance = (hex: string): number => {
  // Убираем # если есть
  const color = hex.replace('#', '');
  
  // Преобразуем hex в RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  
  // Вычисляем относительную яркость по формуле W3C
  // https://www.w3.org/WAI/GL/wiki/Relative_luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance;
};

// Определяем контрастный цвет текста
const getContrastColor = (backgroundColor: string): string => {
  const luminance = getLuminance(backgroundColor);
  // Если фон светлый (luminance > 0.5), используем темный текст, иначе белый
  return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
};

const createLabelRenderer = (chartData: any[]) => (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, index } = props;
  
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Показываем процент только если сегмент достаточно большой
  if (percent < 0.05) return null;

  // Получаем цвет сегмента из chartData по индексу
  const segmentColor = chartData[index]?.color || '#8884d8';
  const textColor = getContrastColor(segmentColor);

  return (
    <text
      x={x}
      y={y}
      fill={textColor}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
      style={{ textShadow: textColor === '#FFFFFF' ? '0 0 3px rgba(0,0,0,0.3)' : 'none' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const PieChart: React.FC<PieChartProps> = ({ data, title }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
        <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400 text-sm">
          Нет данных для отображения
        </div>
      </div>
    );
  }

    const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
        <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400 text-sm">
          Нет данных для отображения
        </div>
      </div>
    );
  }

  const chartData = data.map((item) => ({
    name: item.label,
    value: item.value,
    color: item.color,
    percentage: ((item.value / total) * 100).toFixed(1),
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
  return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{data.name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Значение: <span className="font-semibold">{data.value}</span>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Процент: <span className="font-semibold">{data.payload.percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-col gap-1.5 mt-3">
        {payload.map((entry: any, index: number) => (
          <div
            key={index}
            className="flex items-center justify-between gap-3 px-2 py-0.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div 
                className="w-3 h-3 rounded flex-shrink-0"
                style={{ backgroundColor: entry.color }}
                />
              <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{entry.value}</span>
              </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs font-semibold text-gray-900 dark:text-white">
                {chartData[index]?.value}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">
                {chartData[index]?.percentage}%
              </span>
              </div>
            </div>
          ))}
        </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <div className="w-full" style={{ height: '250px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={createLabelRenderer(chartData)}
              outerRadius={70}
              innerRadius={30}
              fill="#8884d8"
              dataKey="value"
              stroke="#fff"
              strokeWidth={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
